import { useMemo } from "react";

import debounce from "lodash.debounce";
import { Column, Row, Text } from "@bridgesplit/ui";
import { LoopExpanded } from "@bridgesplit/abf-react";
import { LoopRouteType, OracleQuote, TokenListMetadata } from "@bridgesplit/abf-sdk";

import { BalanceDisplay, TokenInputWithPrice, useBalanceChecker, GetAssetButton } from "../../common";
import { useLoopSetWindTokenAmount, getSellTokenForGetAssetButton } from "./util";
import { useWindContext } from "./WindContext";
import { useLoopContext } from "../LoopContext";

export default function WindAmount() {
    const {
        form: { inputAmount, inputToken },
        setForm,
        supportedDepositTokens,
        oraclePrices,
        isInputDisabled
    } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const handleChange = useLoopSetWindTokenAmount();

    const setValueDebounced = useMemo(() => debounce(handleChange, 500), [handleChange]);

    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: inputToken?.mint,
        amount: inputAmount
    });

    function handleInputTokenChange(token: TokenListMetadata) {
        setForm((prev) => ({
            ...prev,
            inputToken: token,
            inputAmount: undefined,
            clmmPosition: undefined
        }));
    }

    const setToken = {
        tokens: supportedDepositTokens,
        setToken: handleInputTokenChange,
        header: ""
    };

    return (
        <Column spacing={0.5}>
            <Row spaceBetween>
                <Text color="caption" variant="body2">
                    You deposit
                </Text>
                <GetAssetButton
                    routeType={loopExpanded?.loopVault.routeType}
                    buyToken={inputToken}
                    sellToken={getSellTokenForGetAssetButton(loopExpanded, inputToken)}
                    supportingToken={loopExpanded?.inputToken} // for orca support
                    isWhirlpoolPoolSelectedInput={loopExpanded?.collateralToken.mint === inputToken?.mint} // for orca support
                    stabblePoolLink={getStabblePoolLink(loopExpanded, oraclePrices)}
                />
            </Row>

            <TokenInputWithPrice
                metadata={inputToken}
                selectToken={supportedDepositTokens && supportedDepositTokens.length > 1 ? setToken : null}
                value={inputAmount}
                setValue={setValueDebounced}
                supportedDepositTokens={supportedDepositTokens}
                loading={isLoading}
                disabled={isInputDisabled}
            />

            <BalanceDisplay
                balance={balance}
                insufficientBalance={insufficientBalance}
                isLoading={isLoading}
                setMax={handleChange}
                metadata={inputToken}
                disabled={isInputDisabled}
            />
        </Column>
    );
}

function getStabblePoolLink(
    loopExpanded: LoopExpanded | undefined,
    oraclePrices: Map<string, OracleQuote | undefined> | undefined
) {
    if (loopExpanded?.loopVault.routeType !== LoopRouteType.Stabble) return undefined;

    const poolId = oraclePrices?.get(loopExpanded.collateralToken.mint)?.oracleAccount;
    return `https://app.stabble.org/liquidity-pools/${poolId}/deposit`;
}
